import { useState, useEffect } from 'react';

export type TransactionStatus = 'idle' | 'pending' | 'confirming' | 'success' | 'error';

interface TransactionToastProps {
  status: TransactionStatus;
  message?: string;
  onDismiss?: () => void;
}

export function TransactionToast({
  status,
  message,
  onDismiss,
}: TransactionToastProps) {
  const [isAnimating, setIsAnimating] = useState(false);

  const isVisible = status !== 'idle';

  useEffect(() => {
    if (isVisible) {
      requestAnimationFrame(() => setIsAnimating(true));
    } else {
      setIsAnimating(false);
    }
  }, [isVisible]);

  // Auto-dismiss success after 3 seconds
  useEffect(() => {
    if (status === 'success' && onDismiss) {
      const timer = setTimeout(onDismiss, 3000);
      return () => clearTimeout(timer);
    }
  }, [status, onDismiss]);

  if (!isVisible && !isAnimating) return null;

  const getStatusConfig = () => {
    switch (status) {
      case 'pending':
        return {
          bgClass: 'bg-gradient-to-r from-blue-600 to-indigo-600',
          icon: (
            <svg className="animate-spin h-6 w-6" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          ),
          title: 'Confirm in Wallet',
          subtitle: message || 'Please confirm the transaction in your wallet',
        };
      case 'confirming':
        return {
          bgClass: 'bg-gradient-to-r from-amber-500 to-orange-500',
          icon: (
            <svg className="animate-spin h-6 w-6" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          ),
          title: 'Transaction Submitted',
          subtitle: message || 'Waiting for blockchain confirmation...',
        };
      case 'success':
        return {
          bgClass: 'bg-gradient-to-r from-green-600 to-emerald-600',
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ),
          title: 'Transaction Confirmed',
          subtitle: message || 'Your transaction was successful!',
        };
      case 'error':
        return {
          bgClass: 'bg-gradient-to-r from-red-600 to-rose-600',
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ),
          title: 'Transaction Failed',
          subtitle: message || 'Something went wrong. Please try again.',
        };
      default:
        return null;
    }
  };

  const config = getStatusConfig();
  if (!config) return null;

  return (
    <div
      className={`
        fixed bottom-0 left-0 right-0 z-50
        transform transition-transform duration-300 ease-out
        ${isAnimating && isVisible ? 'translate-y-0' : 'translate-y-full'}
      `}
    >
      <div className={`${config.bgClass} text-white shadow-lg transition-colors duration-300`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                {config.icon}
              </div>
              <div>
                <p className="font-medium">{config.title}</p>
                <p className="text-sm opacity-90">{config.subtitle}</p>
              </div>
            </div>

            {(status === 'success' || status === 'error') && onDismiss && (
              <button
                onClick={onDismiss}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                aria-label="Dismiss"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
